import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Barcode from "react-barcode";
import styles from "./LoyaltyRewardBrandRefresh.module.css";
import returnIcon from "./imgs/rewards-return.svg";
import scanIcon from "./imgs/rewards-scan.svg";
import markerIcon from "./imgs/rewards-marker.svg";
import { detectPlatform } from "../../shared/utilities/common";
import { PLATFORMS } from "../../shared/constants/common";
import appleWalletBadge from "./imgs/rewards-apple-wallet.png";
import AddToAppleWallet from "../../components/AddToWallet/AddToAppleWallet";
import AddToGoogleWallet from "../../components/AddToWallet/AddToGoogleWallet";

const MAX_PROGRESS_LEVELS = 4;

const selectProgressLevels = (redeemables, balance) => {
  const sorted = (redeemables || [])
    .filter(level => level.points > 0)
    .slice()
    .sort((a, b) => a.points - b.points);

  if (sorted.length <= MAX_PROGRESS_LEVELS) {
    return sorted;
  }

  const nextLevelIndex = sorted.findIndex(level => balance < level.points);
  let start = nextLevelIndex === -1
    ? sorted.length - MAX_PROGRESS_LEVELS
    : Math.max(0, nextLevelIndex - (MAX_PROGRESS_LEVELS - 1));

  if (start + MAX_PROGRESS_LEVELS > sorted.length) {
    start = sorted.length - MAX_PROGRESS_LEVELS;
  }

  return sorted.slice(start, start + MAX_PROGRESS_LEVELS);
};

function LoyaltyCard({ points, redeemables, barcode, barcodeStatus, onRetryBarcode, walletEnabled, onFlip }) {
  const [flipped, setFlipped] = useState(false);
  const barcodeHost = useRef(null);
  const walletControl = useRef(null);
  const isAppleWallet = typeof window !== "undefined" && detectPlatform() === PLATFORMS.IOS;
  const WalletControl = isAppleWallet ? AddToAppleWallet : AddToGoogleWallet;
  const addToWallet = () => walletControl.current && walletControl.current.handleClick();
  useEffect(() => {
    const svg = barcodeHost.current && barcodeHost.current.querySelector("svg");
    if (svg) {
      svg.setAttribute("viewBox", `0 0 ${parseFloat(svg.getAttribute("width"))} ${parseFloat(svg.getAttribute("height"))}`);
      svg.setAttribute("preserveAspectRatio", "none");
    }
  }, [flipped, barcode, barcodeStatus]);
  const balance = Math.max(0, points || 0);
  const levels = selectProgressLevels(redeemables, balance);
  const nextLevel = levels.findIndex(level => balance < level.points);
  let progress = levels.length ? 100 - 50 / levels.length : 0;
  if (nextLevel >= 0) {
    const previous = nextLevel ? levels[nextLevel - 1].points : 0;
    let fraction = (balance - previous) / (levels[nextLevel].points - previous);
    // Interpolate through the supplied 550-point illustration as well as the milestone endpoints.
    if (levels.length === 4 && nextLevel === 1 && previous === 350 && levels[nextLevel].points === 650) {
      const designFraction = (91 - 44.125) / (120.375 - 44.125);
      const anchor = 2 / 3;
      fraction = fraction <= anchor
        ? fraction / anchor * designFraction
        : designFraction + (fraction - anchor) / (1 - anchor) * (1 - designFraction);
    }
    progress = ((nextLevel - 0.5 + fraction) / levels.length) * 100;
    if (nextLevel === 0) progress = (balance / levels[0].points) * (50 / levels.length);
    progress = Math.min(100, progress);
  }

  const lastTierPoints = levels[levels.length - 1]?.points;
  const exceededLastDisplayedTier = lastTierPoints != null && balance > lastTierPoints;

  // The milestone row is inset 6px and 12px within the progress track.
  const markerOffset = (nextLevel === 0 ? 6 * balance / levels[0].points : 6) - 0.18 * progress;
  let progressPosition = "0%";
  if (exceededLastDisplayedTier) {
    progressPosition = "100%";
  } else if (levels.length) {
    progressPosition = `calc(${progress}% + ${markerOffset}px)`;
  }

  const renderBarcode = () => {
    if (barcodeStatus === "loading") {
      return <p role="status">Loading your barcode…</p>;
    }
    if (barcodeStatus === "ready" && barcode) {
      return (
        <div className={styles.barcode} ref={barcodeHost}>
          <Barcode value={barcode} width={1.4} height={87} margin={0} displayValue={false} />
          <p className={styles.barcodeNumber}>{barcode}</p>
        </div>
      );
    }
    return (
      <div role="status" className={styles.barcodeError}>
        <p>Your barcode couldn’t be loaded.</p>
        <button type="button" className={styles.retryButton} onClick={onRetryBarcode}>Try again</button>
      </div>
    );
  };

  return (
    <div className={styles.cardGroup}>
      <div className={styles.cardContainer}>
        <button
          type="button"
          className={`${styles.flipButton} ${flipped ? styles.flipButtonFlipped : ""}`}
          aria-label={flipped ? "Show rewards points" : "Show rewards barcode"}
          aria-pressed={flipped}
          onClick={() => { setFlipped(!flipped); onFlip(!flipped); }}
        >
          {flipped ? (
            <span className={styles.returnControl}><img src={returnIcon} alt="" aria-hidden="true" width="24" height="24" /></span>
        ) : (
          <span className={styles.scanBadge}>
            <span className={styles.scanLabel}>Scan</span>
            <span className={styles.scanIcon}><img src={scanIcon} alt="" aria-hidden="true" width="24" height="24" /></span>
          </span>
        )}
        </button>
        <div className={`${styles.cardInner} ${flipped ? styles.cardFlipped : ""}`}>
          <div className={styles.cardFront} aria-hidden={flipped}>
            <div className={styles.pointsBalance}>{balance}</div>
            <p className={styles.pointsLabel}>Reward points</p>
            {levels.length > 0 && (
            <div className={styles.progressContainer}>
              <div
                className={styles.progressTrack}
                role="progressbar"
                aria-label="Rewards points progress"
                aria-valuemin={0}
                aria-valuemax={levels[levels.length - 1].points}
                aria-valuenow={Math.min(balance, levels[levels.length - 1].points)}
                aria-valuetext={`${balance} reward points`}
              >
                <img src={markerIcon} alt="" className={styles.progressMarker} style={{ left: progressPosition }} aria-hidden="true" />
                <div className={styles.progressFill} style={{ width: progressPosition }} />
              </div>
              <div className={styles.progressLevels}>
                {levels.map(level => (
                  <div key={level.id || level.points} className={styles.progressLevel}>
                    <span className={`${styles.progressDot} ${balance >= level.points ? styles.unlockedDot : ""}`} />
                    <span>{level.points.toLocaleString("en-US")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
          <div className={styles.cardBack} aria-hidden={!flipped}>
            {flipped && (
            <>
              <p className={styles.visuallyHidden}>Rewards number</p>
              {barcodeStatus === "ready" && barcode ? renderBarcode() : null}
            </>
          )}
          </div>
        </div>
        {flipped && (barcodeStatus !== "ready" || !barcode) && <div className={styles.barcodeStatusOverlay}>{renderBarcode()}</div>}
      </div>
      {flipped && walletEnabled && barcodeStatus === "ready" && barcode && (
        <div
          className={`${styles.walletAction} ${isAppleWallet ? styles.appleWalletAction : ""}`}
          role="button"
          tabIndex={0}
          aria-label={isAppleWallet ? "Add to Apple Wallet" : "Add to Google Wallet"}
          onClick={event => { if (event.target.tagName !== "IMG") addToWallet(); }}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              addToWallet();
            }
          }}
        >
          <WalletControl ref={walletControl} badgeImage={isAppleWallet ? appleWalletBadge : undefined} />
        </div>
      )}
    </div>
  );
}

LoyaltyCard.propTypes = {
  points: PropTypes.number,
  redeemables: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, points: PropTypes.number })),
  barcode: PropTypes.string,
  barcodeStatus: PropTypes.oneOf(["loading", "ready", "error"]),
  walletEnabled: PropTypes.bool,
  onFlip: PropTypes.func,
  onRetryBarcode: PropTypes.func
};

LoyaltyCard.defaultProps = {
  walletEnabled: false,
  onFlip: () => {},
  points: 0,
  redeemables: null,
  barcode: null,
  barcodeStatus: "loading",
  onRetryBarcode: () => {}
};

export default LoyaltyCard;
